"use client";

import dynamic from "next/dynamic";
import { TooltipProvider } from "@/components/ui/tooltip";
import { items, facilities } from "@/data";
import { useProductionPlan } from "@/hooks/useProductionPlan";
import AppHeader from "@/components/layout/AppHeader";
import LeftPanel from "@/components/panels/LeftPanel";
import ProductionViewTabs from "@/components/production/ProductionViewTabs";
import AppFooter from "@/components/layout/AppFooter";
import "@/i18n";

const AddTargetDialogGrid = dynamic(
  () => import("@/components/panels/AddTargetDialogGrid"),
  {
    ssr: false,
    loading: () => null,
  },
);

type CalculatorAppProps = {
  embedded?: boolean;
};

export default function CalculatorApp({ embedded = false }: CalculatorAppProps) {
  const {
    targets,
    dialogOpen,
    activeTab,
    plan,
    tableData,
    stats,
    error,
    handleTargetChange,
    handleTargetRemove,
    handleAddTarget,
    handleToggleRawMaterial,
    handleRecipeChange,
    handleAddClick,
    setDialogOpen,
    setActiveTab,
  } = useProductionPlan();

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-4">
        {!embedded && <AppHeader />}

        <div className="flex-1 flex flex-col gap-4 min-h-0 lg:flex-row">
          <LeftPanel
            targets={targets}
            items={items}
            facilities={facilities}
            totalPowerConsumption={stats.totalPowerConsumption}
            productionSteps={stats.uniqueProductionSteps}
            rawMaterialCount={stats.rawMaterialRequirements.size}
            facilityRequirements={stats.facilityRequirements}
            error={error}
            onTargetChange={handleTargetChange}
            onTargetRemove={handleTargetRemove}
            onAddClick={handleAddClick}
          />

          <ProductionViewTabs
            plan={plan}
            tableData={tableData}
            items={items}
            facilities={facilities}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onRecipeChange={handleRecipeChange}
            onToggleRawMaterial={handleToggleRawMaterial}
          />
        </div>

        {dialogOpen && (
          <AddTargetDialogGrid
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            items={items}
            existingTargetIds={targets.map((t) => t.itemId)}
            onAddTarget={handleAddTarget}
          />
        )}

        {!embedded && <AppFooter />}
      </div>
    </TooltipProvider>
  );
}
